import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Flame, Feather, Calendar, Music2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import PoemDisplay from '../components/PoemDisplay';
import { POEMS, EMOTIONS, getPoemOfTheWeek, getPoemsByEmotion } from '../data/poems';

const PoetryRoom = () => {
  const potw = useMemo(() => getPoemOfTheWeek(), []);
  const [selectedEmotion, setSelectedEmotion] = useState(null);

  const matches = selectedEmotion ? getPoemsByEmotion(selectedEmotion) : [];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header bar */}
      <header className="sticky top-0 z-50 bg-[#0a0a0a]/90 backdrop-blur-lg border-b border-[#D4AF37]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-gray-400 hover:text-[#D4AF37] transition-colors" data-testid="poetry-back-home">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back home</span>
          </Link>
          <div className="flex items-center gap-2">
            <Music2 className="w-5 h-5 text-[#D4AF37]" />
            <span className="text-white font-bold" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
              40 SONGS AND A MIC
            </span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#D4AF37]/5 via-transparent to-transparent" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-4 py-1 mb-6">
            <Feather className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-[#D4AF37] font-bold text-xs tracking-wider">THE POETRY ROOM</span>
          </div>
          <h1
            className="text-5xl sm:text-6xl md:text-7xl font-bold text-white mb-6 leading-tight"
            style={{ fontFamily: '"Bebas Neue", sans-serif' }}
          >
            POEMS FOR THE{' '}
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#FFD700]">
              MOMENT YOU ARE IN
            </span>
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
            Verses from J&rsquo;Wells, our founder. One new poem featured every week.
            Or pick what you are carrying today and let a poem meet you there.
          </p>
        </div>
      </section>

      {/* Poem of the Week */}
      <section className="pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <Calendar className="w-6 h-6 text-[#D4AF37]" />
            <h2 className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
              POEM OF THE WEEK
            </h2>
          </div>
          <div data-testid="poem-of-the-week">
            <PoemDisplay poem={potw} size="lg" showShare showPermalink />
          </div>
        </div>
      </section>

      {/* Emotion picker */}
      <section className="pb-24 border-t border-[#D4AF37]/10 pt-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center space-x-2 bg-[#E63946]/10 border border-[#E63946]/30 rounded-full px-4 py-1 mb-4">
              <Flame className="w-4 h-4 text-[#E63946]" />
              <span className="text-[#E63946] font-bold text-xs tracking-wider">POEM FOR THE MOMENT</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-3" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
              What are you carrying right now?
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Tap the one closest to how you feel. We will find a verse that meets you there.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3 mb-12" data-testid="emotion-picker">
            {EMOTIONS.map((emo) => {
              const isActive = selectedEmotion === emo.id;
              const count = getPoemsByEmotion(emo.id).length;
              return (
                <button
                  key={emo.id}
                  onClick={() => setSelectedEmotion(isActive ? null : emo.id)}
                  data-testid={`emotion-${emo.id}`}
                  disabled={count === 0}
                  className={`px-5 py-3 rounded-full font-medium text-sm transition-all duration-200 ${
                    isActive
                      ? `bg-gradient-to-r ${emo.color} text-white shadow-lg scale-105`
                      : count === 0
                      ? 'bg-[#1a1a1a] text-gray-600 border border-[#1a1a1a] cursor-not-allowed'
                      : 'bg-[#1a1a1a] text-gray-300 border border-[#D4AF37]/20 hover:border-[#D4AF37] hover:text-white'
                  }`}
                >
                  {emo.label}
                  {count > 0 && <span className="ml-2 opacity-60 text-xs">({count})</span>}
                </button>
              );
            })}
          </div>

          {selectedEmotion && (
            <div className="space-y-6" data-testid="emotion-results">
              {matches.length === 0 ? (
                <p className="text-center text-gray-500 py-10">
                  No poem is tagged with this emotion yet. Check back soon.
                </p>
              ) : (
                matches.map((poem) => <PoemDisplay key={poem.id} poem={poem} showShare showPermalink />)
              )}
            </div>
          )}

          {!selectedEmotion && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {POEMS.slice(0, 4).map((poem) => (
                <PoemDisplay key={poem.id} poem={poem} showShare showPermalink />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center bg-gradient-to-r from-[#1a1a1a] via-[#D4AF37]/5 to-[#1a1a1a] border border-[#D4AF37]/30 rounded-3xl p-10">
          <h3 className="text-3xl md:text-4xl font-bold text-white mb-3" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
            A verse moved you?
          </h3>
          <p className="text-gray-400 max-w-2xl mx-auto mb-8">
            The full curriculum behind these words lives in the Gasoline workbooks.
          </p>
          <Link to="/#workbooks" className="inline-block max-w-full">
            <Button
              data-testid="poetry-cta-workbooks"
              className="w-full sm:w-auto max-w-full bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0a0a0a] hover:from-[#FFD700] hover:to-[#D4AF37] font-bold px-6 sm:px-8 py-6 text-base sm:text-lg whitespace-normal leading-tight break-words"
            >
              EXPLORE THE GASOLINE SERIES
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PoetryRoom;
